// src/store/uiFrameStore.ts
// uiFrameStore — Zustand state layer for UI state projection.
//
// Responsibilities:
//   - Subscribe to FrameCache events
//   - Hold minimal UI projection state (NOT full frames)
//   - Provide selectors for components
//
// Does NOT do: WS connection, caching, diffing

import { create } from 'zustand';
import type { FrameCache, Frame } from '../core/frame_cache';
import type { UIFrame } from '../core/ui_contract';

export type AppMode = 'live' | 'replay';

export interface UIFrameState {
  // Mode
  mode: AppMode;

  // Connection
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

  // Current frame reference (NOT full copy)
  currentFrameId: number;    // 1-based frame position in timeline
  latestFrame: UIFrame | null;

  // Replay state
  replayPointer: number;     // current frame_id during replay
  isPlaying: boolean;

  // Stats
  totalFrames: number;
  lastSeq: number;
  // R9-ext 2026-06-24: backend-side generated_at_ms of the most
  // recent frame. Surfaced as the dashboard "更新时间" timestamp.
  // null until the first frame arrives.
  lastFrameTs: number | null;
}

interface UIFrameActions {
  // Frame updates (called by wsClient event)
  onFrame(frame: Frame): void;

  // Connection updates (called by wsClient event)
  onConnectionStatus(status: UIFrameState['connectionStatus']): void;

  // Mode
  setMode(mode: AppMode): void;

  // Replay controls
  seekTo(frameId: number): void;
  play(): void;
  pause(): void;
  stepForward(): void;
  stepBackward(): void;

  // Internal
  _init(frameCache: FrameCache): () => void;
}

type UIFrameStore = UIFrameState & UIFrameActions;

export const useUIFrameStore = create<UIFrameStore>((set, get) => ({
  // Initial state
  mode: 'live',
  connectionStatus: 'disconnected',
  currentFrameId: 0,
  latestFrame: null,
  replayPointer: 0,
  isPlaying: false,
  totalFrames: 0,
  lastSeq: 0,
  lastFrameTs: null,

  // ---- Frame Events ----

  onFrame(frame: Frame) {
    set({
      latestFrame: frame.data,
      currentFrameId: frame.frame_id,
      lastSeq: frame.seq,
      totalFrames: frame.frame_id, // frame_id = total count (1-based)
      // R9-ext 2026-06-24: use the backend's generated_at_ms as the
      // dashboard "last update" timestamp. Previously lastUpdate was
      // mapped to lastSeq (a server sequence number, monotonic but
      // not a timestamp), which made fmt(new Date(seq)) render
      // "1970-01-01 00:00:00 GMT" → "19:00:00 GMT+8" — the dashboard
      // header looked frozen even when frames were flowing in.
      lastFrameTs: frame.generated_at_ms ?? null,
    });
  },

  onConnectionStatus(status) {
    set({ connectionStatus: status });
  },

  // ---- Mode ----

  setMode(mode) {
    set({ mode, isPlaying: false, replayPointer: 0 });
  },

  // ---- Replay Controls ----

  seekTo(frameId) {
    const { mode } = get();
    if (mode !== 'replay') return;
    set({ replayPointer: frameId });
  },

  play() {
    const { mode } = get();
    if (mode !== 'replay') return;
    set({ isPlaying: true });
  },

  pause() {
    set({ isPlaying: false });
  },

  stepForward() {
    const { replayPointer, totalFrames, mode } = get();
    if (mode !== 'replay') return;
    if (replayPointer < totalFrames) {
      set({ replayPointer: replayPointer + 1 });
    }
  },

  stepBackward() {
    const { replayPointer, mode } = get();
    if (mode !== 'replay') return;
    if (replayPointer > 1) {
      set({ replayPointer: replayPointer - 1 });
    }
  },

  // ---- Init: Subscribe to FrameCache ----

  _init(frameCache: FrameCache) {
    // Subscribe to new frames
    const onFrameAdded = (frame: Frame) => {
      get().onFrame(frame);
    };

    frameCache.on('frame:added', onFrameAdded);

    // Return unsubscribe
    return () => {
      frameCache.off('frame:added', onFrameAdded);
    };
  },
}));

// ---- Selectors ----

// Core selectors — components should use these, not direct store access
export const selectLatestFrame = (state: UIFrameStore) => state.latestFrame;
export const selectConnectionStatus = (state: UIFrameStore) => state.connectionStatus;
export const selectMode = (state: UIFrameStore) => state.mode;
export const selectTotalFrames = (state: UIFrameStore) => state.totalFrames;
export const selectCurrentFrameId = (state: UIFrameStore) => state.currentFrameId;

// Replay selectors
export const selectReplayPointer = (state: UIFrameStore) => state.replayPointer;
export const selectIsPlaying = (state: UIFrameStore) => state.isPlaying;

// Derived selectors (use with caution — may cause unnecessary rerenders)
export const selectMarket = (state: UIFrameStore) => state.latestFrame?.market;
export const selectSignals = (state: UIFrameStore) => state.latestFrame?.signals;
export const selectBroker = (state: UIFrameStore) => state.latestFrame?.broker;
export const selectExecution = (state: UIFrameStore) => state.latestFrame?.execution;
export const selectMetrics = (state: UIFrameStore) => state.latestFrame?.metrics;
export const selectGateStatus = (state: UIFrameStore) => state.latestFrame?.gate_status;

// 2026-06-22 commit #3: selectAuth — declared by useAuthFromFrame.ts
// as the "canonical authenticated signal" once a /ws/ui frame arrives.
// Currently returns an unauthenticated snapshot because UIFrame does
// not carry an auth field. AuthGate.tsx:38 reads auth.valid to decide
// whether to render children inline vs render <Login />; since
// useAuthFromFrame returns null when no frame has arrived (its first
// branch), the second branch (auth.valid check) only fires when a
// frame annotates an auth snapshot — which the backend does not yet
// do. So today the AuthGate's `auth.valid` check is unreachable in
// practice; it's wired in to be ready for backend support.
export const selectAuth = (_state: UIFrameStore): import('../core/ui_contract').AuthSnapshot => ({
  user: null,
  valid: false,
});
